<template>
  <section class="container mx-auto px-4 md:px-8 py-8">
    <!-- Breadcrumbs -->
    <nav aria-label="Breadcrumb" class="mb-4 text-sm text-gray-500">
      <ol class="flex flex-wrap items-center gap-1">
        <li>
          <NuxtLink to="/" class="hover:text-black transition"
            >Головна</NuxtLink
          >
          <span class="mx-1">/</span>
        </li>
        <li>
          <NuxtLink to="/catalog" class="hover:text-black transition"
            >Каталог</NuxtLink
          >
          <span class="mx-1">/</span>
        </li>
        <li v-if="categoryBreadcrumb">
          <NuxtLink
            :to="categoryBreadcrumb.href"
            class="hover:text-black transition"
          >
            {{ categoryBreadcrumb.label }}
          </NuxtLink>
          <span class="mx-1">/</span>
        </li>
        <li
          class="text-black font-medium truncate max-w-[200px] sm:max-w-none"
          aria-current="page"
        >
          {{ product.title || "Товар" }}
        </li>
      </ol>
    </nav>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Галерея (LCP) -->
      <div class="relative">
        <ProductGallery :images="gallery" />
        <div
          v-if="isExclusive || isLonelyBeauty"
          class="absolute left-5 top-5 z-20 flex flex-col items-start gap-1.5"
        >
          <div
            v-if="isExclusive"
            class="rounded-lg bg-pink-200/90 px-2.5 py-1 text-xs font-semibold text-black"
          >
            {{ exclusiveBadgeText }}
          </div>
          <div
            v-if="isLonelyBeauty"
            class="rounded-lg bg-pink-200/90 px-2.5 py-1 text-xs font-semibold text-black"
          >
            Самотня красуня
          </div>
        </div>
        <button
          class="absolute right-3 top-2 z-20 inline-flex items-center justify-center rounded-full w-12 h-12 hover:scale-105 active:scale-95 disabled:opacity-50"
          :aria-pressed="isWished ? 'true' : 'false'"
          type="button"
          :disabled="!canWishlist"
          :title="wishlistTitle"
          @click="toggleWishlist"
        >
          <Heart
            v-if="isWished"
            :size="27"
            fill="currentColor"
            class="text-pink-200"
          />
          <Heart v-else :size="25" class="text-gray-00" />
        </button>
      </div>

      <!-- Правий стовпчик -->
      <div>
        <h1 class="text-2xl md:text-3xl font-bold mb-3">{{ product.title }}</h1>

        <div
          class="flex md:flex-row flex-col items-start md:items-center gap-4 mb-5"
        >
          <div class="text-xl md:text-2xl font-extrabold">
            {{ priceToShow }} грн
          </div>
          <div v-if="skuMasked" class="text-xs text-gray-400">
            Код: {{ skuMasked }}
          </div>
          <div
            v-if="inStock"
            class="text-sm text-green-600 flex items-center gap-1"
          >
            <Check :size="16" /> В наявності
          </div>
          <div v-else class="text-sm text-red-600">Немає в наявності</div>
        </div>

        <!-- Колір -->
        <div v-if="(product.colors || []).length" class="mb-5">
          <div class="text-sm text-gray-600 mb-2">Колір:</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="c in product.colors"
              :key="getColorValue(c)"
              type="button"
              class="px-3 py-1 border rounded-full text-sm transition"
              :class="
                selectedColor === getColorValue(c)
                  ? 'border-black'
                  : 'border-gray-200 hover:border-black/50'
              "
              :aria-pressed="selectedColor === getColorValue(c)"
              :title="c.label || getColorValue(c)"
              @click="selectedColor = getColorValue(c)"
            >
              {{ c.label || getColorValue(c) }}
            </button>
          </div>
        </div>

        <!-- Розмір -->
        <div v-if="(product.sizes || []).length" class="mb-5">
          <div class="text-sm text-gray-600 mb-2">Розмір:</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="s in product.sizes"
              :key="s"
              type="button"
              class="px-3 py-1 border rounded text-sm transition"
              :class="
                selectedSize === s
                  ? 'border-black'
                  : 'border-gray-200 hover:border-black/50'
              "
              :aria-pressed="selectedSize === s"
              @click="selectedSize = s"
            >
              {{ s }}
            </button>
          </div>
        </div>

        <!-- Лот (звичайний / ексклюзив) -->
        <div v-if="lotOptions.length" class="mb-5">
          <div class="text-sm text-gray-600 mb-2">Лот:</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="opt in lotOptions"
              :key="opt.id"
              type="button"
              class="px-3 py-1 border rounded-full text-sm transition"
              :class="
                selectedVariantId === opt.id
                  ? 'border-black'
                  : 'border-gray-200 hover:border-black/50'
              "
              :aria-pressed="selectedVariantId === opt.id"
              :title="opt.title"
              @click="selectLot(opt.id)"
            >
              {{ opt.label }}
            </button>
          </div>
          <div v-if="lotHint" class="text-xs text-gray-500 mt-2">
            {{ lotHint }}
          </div>
        </div>

        <!-- Кількість -->
        <div class="mb-6">
          <div class="text-sm text-gray-600 mb-2">Кількість:</div>
          <div class="inline-flex items-center border rounded">
            <button
              class="px-3 py-2"
              aria-label="Зменшити"
              @click="qty = Math.max(1, Math.min(maxSelectableQty, qty - 1))"
            >
              −
            </button>
            <input
              class="w-12 text-center outline-none"
              type="number"
              min="1"
              :value="qty"
              @input="onQtyInput"
            />
            <button
              class="px-3 py-2"
              :disabled="!inStock"
              aria-label="Збільшити"
              @click="onIncQty"
            >
              +
            </button>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3 mb-6">
          <button
            class="px-6 py-3 rounded-xl font-semibold transition bg-pink-200 text-black hover:bg-pink-200/80 disabled:opacity-50"
            :disabled="!canAdd"
            @click="addToCart"
          >
            Додати в кошик
          </button>
        </div>

        <!-- Бейджі та тексти нижче згину -->
        <div
          class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-sm below-fold"
        >
          <div class="flex items-center gap-2 border rounded-lg p-3">
            <Truck :size="18" /> Швидка доставка
          </div>
          <div class="flex items-center gap-2 border rounded-lg p-3">
            <Package :size="18" /> Безпечне пакування
          </div>
          <div class="flex items-center gap-2 border rounded-lg p-3">
            <Leaf :size="18" /> Натуральні матеріали
          </div>
        </div>

        <div class="prose max-w-none below-fold">
          <h2 class="text-lg font-semibold mb-2">Опис</h2>
          <p class="text-gray-700 mb-4">{{ product.description }}</p>
          <h3 class="text-base font-semibold mb-1">Догляд</h3>
          <ul class="list-disc ml-6 text-gray-700 mb-4">
            <li v-for="i in product.care" :key="i">{{ i }}</li>
          </ul>
          <h3 class="text-base font-semibold mb-1">Доставка та оплата</h3>
          <ul class="list-disc ml-6 text-gray-700">
            <li>Нова Пошта по Україні, 2-5 днів</li>
            <li>Оплата онлайн чи накладений платіж</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { Truck, Package, Check, Leaf, Heart } from "lucide-vue-next";
import ProductGallery from "~/components/product/ProductGallery.vue";
import { useWishlistStore } from "~/stores/wishlist";
import { useProductFetch } from "~/composables/product/useProductFetch";
import { useProductVariants } from "~/composables/product/useProductVariants";
import { useProductGallery } from "~/composables/product/useProductGallery";
import { useProductCart } from "~/composables/product/useProductCart";
import { useProductTracking } from "~/composables/product/useProductTracking";
import { useProductSeo } from "~/composables/product/useProductSeo";
import { useCategoriesData } from "~/composables/useCategoriesData";

definePageMeta({ ssr: true });

/* ---- Data ---- */
const { product, siteUrl } = await useProductFetch();

/* ---- Categories — публічне джерело правди (SSR + кеш) ---- */
const { cats: categoriesData } = useCategoriesData();

const categoryBreadcrumb = computed(() => {
  const slug = product.value?.categorySlug;
  if (!slug) return null;
  const cats = categoriesData.value || [];
  const cat = cats.find((c) => c.slug === slug);
  if (!cat) return null;
  // Dedicated SEO landing pages
  const LANDING_PAGES = { pillowcases: "/catalog/pillowcases" };
  return {
    label: cat.label,
    href: LANDING_PAGES[slug] || `/catalog?cat=${slug}`,
  };
});

/* ---- Variants ---- */
const {
  selectedColor,
  selectedSize,
  selectedVariantId,
  matchingVariants,
  currentVariant,
  isExclusive,
  exclusiveBadgeText,
  lotOptions,
  lotHint,
  selectLot,
  getColorValue,
  priceToShow,
  inStock,
  isLonelyBeauty,
  mustChooseOptions,
  variantSku,
  skuMasked,
} = useProductVariants(product);

/* ---- Gallery ---- */
const { gallery } = useProductGallery(product, currentVariant);

/* ---- Cart ---- */
const { qty, canAdd, addToCart, onQtyInput, onIncQty, maxSelectableQty } =
  useProductCart({
    product,
    currentVariant,
    selectedColor,
    selectedSize,
    matchingVariants,
    gallery,
    priceToShow,
    inStock,
    variantSku,
    mustChooseOptions,
  });

/* ---- Wishlist ---- */
const wl = useWishlistStore();
const canWishlist = computed(
  () => !mustChooseOptions.value || !!currentVariant.value,
);
const isWished = computed(
  () =>
    !!currentVariant.value?.id &&
    wl.hasPair(product.value.id, currentVariant.value.id),
);
const wishlistTitle = computed(() =>
  !canWishlist.value
    ? "Оберіть колір та розмір"
    : isWished.value
      ? "Прибрати з бажаного"
      : "Додати в бажане",
);
async function toggleWishlist() {
  if (!canWishlist.value || !currentVariant.value?.id) return;
  await wl.togglePair(product.value.id, currentVariant.value.id);
}

/* ---- Tracking & SEO ---- */
useProductTracking({
  product,
  currentVariant,
  selectedColor,
  selectedSize,
  priceToShow,
});
useProductSeo({
  product,
  currentVariant,
  gallery,
  priceToShow,
  inStock,
  siteUrl,
  categories: categoriesData,
});
</script>

<style scoped>
/* секції нижче згину — не заважають LCP та не спричиняють CLS */
.below-fold {
  content-visibility: auto;
  contain-intrinsic-size: 600px 800px;
}
@media (min-width: 1024px) {
  .below-fold {
    contain-intrinsic-size: 400px 500px;
  }
}
</style>
